import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, CheckCircle2, Gauge, Lock, Mail, Sparkles, Users } from "lucide-react";
import { ProBrandLogo } from "@/components/BrandLogo";
import { ProFooter } from "@/components/login/ProFooter";
import { FullSpinner } from "@/components/Common";
import { EmailAuth } from "@/components/login/EmailAuth";
import { ErrorNote, Field, GoldButton, Strength, authErr } from "@/components/login/AuthBits";
import { GoogleButton } from "@/components/login/SignInCard";
import { PricingCards } from "@/components/login/PricingCards";
import { ProductDemo } from "@/components/demo/ProductDemo";
import { setDemoLink, unb64 } from "@/lib/demoTrack";

const DemoSection = () => {
  const [q] = useSearchParams();
  const { status } = useAuth();
  const visitor = status === "anon";
  const token = q.get("d");
  const [who, setWho] = useState(() => ({ company: unb64(q.get("c") || ""), name: "" }));
  useEffect(() => {
    if (token && visitor) setDemoLink(token);
  }, [token, visitor]);
  useEffect(() => {
    if (!token) return;
    api.get(`/public/demo-links/${encodeURIComponent(token)}`).then((r) => r.data?.company && setWho({ company: r.data.company, name: r.data.name || "" })).catch(() => {});
    setTimeout(() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" }), 600);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const first = who.name.split(" ")[0];
  return (
  <section id="demo" className="relative z-[2] mx-auto max-w-6xl scroll-mt-20 px-4 pb-6 pt-10 sm:px-6 lg:px-8" data-testid="demo-section">
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D4AF37]">{who.company ? `Made for ${who.company}` : "See it work · under 4 minutes"}</p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-zinc-50 sm:text-4xl" data-testid="demo-heading">{who.company ? `${first ? `${first}, here's` : "Here's"} how ${who.company} turns signals into placement fees.` : "From signal to placement fee."}</h2></div>
      <p className="max-w-md text-sm text-zinc-400 md:text-base">Watch a recruiter find a hiring company, reach the decision maker, source the shortlist, and see what it's worth.</p>
    </div>
    <ProductDemo firm={who.company} track={visitor} />
  </section>
  );
};
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CLIENT_RETURN, clientHome } from "@/lib/clients";
import { PRO_TITLE, SALES_EMAIL } from "@/lib/clientHost";

const portal = () => localStorage.setItem("rpopro_portal", "client");
const google = () => { portal(); sessionStorage.setItem("rpopro_return", CLIENT_RETURN); window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(`${window.location.origin}/search`)}`; };

const Card = ({ children, className = "" }) => (
  <div className={`signin-card relative w-full rounded-[28px] p-[1.5px] ${className}`}><div className="rounded-[27px] bg-[#0E0E11]/95 p-6 backdrop-blur-xl sm:p-8">
    <p className="mb-1 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D4AF37]"><Lock className="h-3 w-3" />Client portal</p>
    {children}
  </div></div>
);

const Glow = () => <>
  <div className="lp-glow pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#D4AF37]/10 blur-[140px]" />
  <div className="lp-glow pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#8224e3]/15 blur-[140px]" />
</>;

const Shell = ({ children, testid }) => (
  <div className="grain relative flex min-h-screen items-center justify-center overflow-clip bg-[#09090B] p-4" data-testid={testid}>
    <Glow />
    <div className="fade-up w-full max-w-md"><ProBrandLogo size="md" className="mb-5" /><Card>{children}</Card></div>
  </div>
);

const Or = () => <div className="my-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600"><span className="h-px flex-1 bg-[#27272A]" />or use email<span className="h-px flex-1 bg-[#27272A]" /></div>;

const POINTS = [[Gauge, "0-100 opportunity scores", "with the reason to call now"], [Users, "Decision makers & talent", "found for you, every week"], [Sparkles, "AI workflows", "describe it in plain words"]];

const Hero = () => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D4AF37]">For Recruiters & Sales Professionals</p>
    <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl" data-testid="client-hero-title">Turn Sourcing Intelligence<br /><span className="bg-gradient-to-r from-[#FFE7A0] via-[#F3C649] to-[#D4AF37] bg-clip-text text-transparent">Into Revenue.</span></h1>
    <p className="mt-5 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base" data-testid="client-hero-copy">Find the right talent. Identify the right companies. Uncover the right contacts. MyNextBigMove.co brings sourcing and revenue intelligence together in one command center, helping recruiters cut through the noise, prioritize the best opportunities, and turn recruiting data into conversations, pipeline, and revenue.</p>
    <div className="mt-8 grid gap-3 sm:grid-cols-3">{POINTS.map(([I, t, s], i) => (
      <motion.div key={t} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md">
        <I className="h-5 w-5 text-[#F3C649]" /><p className="mt-3 text-sm font-semibold text-zinc-100">{t}</p><p className="text-xs text-zinc-500">{s}</p></motion.div>))}</div>
    <a href="#pricing" className="mt-8 hidden items-center gap-1.5 text-sm text-[#c79bff] transition-colors hover:text-[#e4ccff] lg:inline-flex" data-testid="client-see-pricing">See plans & pricing ↓</a>
  </motion.div>
);

// §78/§82 Client login with public pricing.
export default function ClientLogin() {
  const { status } = useAuth();
  const [sp] = useSearchParams();
  useEffect(() => { portal(); const t = document.title; document.title = PRO_TITLE; return () => { document.title = t; }; }, []);
  if (status === "authed" && !sp.get("d")) return <Navigate to={CLIENT_RETURN} replace />;
  return (
    <div className="grain relative min-h-screen overflow-clip bg-[#09090B]" data-testid="client-login">
      <Glow />
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#09090B]/75 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <a href="#top" aria-label="MyNextBigMove.co home" data-testid="pro-header-logo"><ProBrandLogo size="sm" className="sm:hidden" tagline={false} /><ProBrandLogo size="sm" className="hidden sm:inline-flex" testid="pro-logo" /></a>
          <nav className="flex items-center gap-1 text-sm"><a href="#demo" className="hidden rounded-full px-3 py-1.5 text-zinc-400 transition-colors hover:text-zinc-50 sm:inline" data-testid="client-nav-demo">Product demo</a><a href="#pricing" className="rounded-full px-3 py-1.5 text-zinc-400 transition-colors hover:text-zinc-50" data-testid="client-nav-pricing">Pricing</a><a href="#signin" className="rounded-full border border-[#D4AF37]/50 px-4 py-1.5 text-[#F3C649] transition-colors hover:bg-[#D4AF37] hover:text-black" data-testid="client-nav-signin">Sign in</a></nav></div>
      </header>
      <section id="top" className="relative z-[2] mx-auto grid max-w-7xl items-center gap-12 px-4 pb-10 pt-12 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-20">
        <Hero />
        <div id="signin" className="order-first scroll-mt-24 lg:order-none lg:col-span-5">
          <Card>
            <h2 className="font-display text-2xl font-semibold text-zinc-50">Sign in to your workspace</h2>
            <p className="mb-6 mt-1.5 text-sm text-zinc-400">For invited clients. Use the email your invite was sent to.</p>
            <GoogleButton onClick={google} testid="client-google" />
            <Or />
            <EmailAuth signInOnly />
            <p className="mt-6 text-center text-[11px] text-zinc-500">No account yet? <a href="#pricing" className="text-[#F3C649] hover:underline">Pick a plan</a> to start today, or request a demo.</p>
            <p className="mt-2 text-center text-[11px] text-zinc-500" data-testid="client-contact-sales">Questions? Email <a href={`mailto:${SALES_EMAIL}`} className="text-[#F3C649] hover:underline">{SALES_EMAIL}</a></p>
          </Card>
        </div>
      </section>
      <DemoSection />
      <PricingCards />
      <ProFooter />
    </div>
  );
}

export const ClientHome = () => {
  const { client } = useAuth();
  sessionStorage.removeItem("rpopro_return");
  return <Navigate to={client ? clientHome(client) : "/search"} replace />;
};

// Signed in from the client portal but the account has no client workspace.
export const ClientNoWorkspace = () => {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const out = async () => { await logout(); nav("/client/login", { replace: true }); };
  const app = () => { localStorage.removeItem("rpopro_portal"); nav(isAdmin ? "/admin" : "/search", { replace: true }); };
  return (
    <Shell testid="client-no-workspace">
      <p className="font-display text-xl text-zinc-50">No client workspace for this account</p>
      <p className="mt-2 text-sm text-zinc-400"><span className="text-zinc-200" data-testid="client-no-workspace-email">{user?.email}</span> isn't linked to a Revenue Command Center or Sourcing Intelligence workspace yet. Sign in with the email your invite was sent to, or ask your contact to send an invite.</p>
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={out} className="rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-semibold text-black hover:bg-[#F3C649]" data-testid="client-no-workspace-signout">Sign in with another email</button>
        <a href="mailto:sales@mynextbigmove.co?subject=Client%20workspace%20access" className="inline-flex items-center gap-1.5 rounded-full border border-[#27272A] px-4 py-2 text-sm text-zinc-300 hover:text-zinc-50" data-testid="client-no-workspace-contact"><Mail className="h-4 w-4" />Contact us</a>
      </div>
      <button onClick={app} className="mt-5 text-xs text-zinc-500 hover:text-zinc-300" data-testid="client-no-workspace-app">{isAdmin ? "Open the admin app instead" : "I'm a job seeker — open the MyNextBigMove app"}</button>
    </Shell>
  );
};

// Invite link: /client/accept?token=… — set a password (or continue with Google using the invited email).
export const ClientAccept = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { loginWithPassword } = useAuth();
  const [inv, setInv] = useState(null);
  const [p, setP] = useState(""); const [c, setC] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEffect(() => { portal(); if (!token) return setInv({ bad: true }); api.get(`/auth/invite?token=${encodeURIComponent(token)}`).then((r) => setInv(r.data)).catch(() => setInv({ bad: true })); }, [token]);
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (p.length < 8) return setErr("Use at least 8 characters.");
    if (p !== c) return setErr("The two passwords don't match.");
    setBusy(true);
    try { await loginWithPassword("accept-invite", { token, password: p }); nav(CLIENT_RETURN, { replace: true }); }
    catch (x) { setErr([400, 401, 410].includes(x?.response?.status) ? "This invite has expired or was already used. Ask your RPOPRO contact to resend it." : authErr(x)); setBusy(false); }
  };
  if (!inv) return <FullSpinner />;
  if (inv.bad) return <Shell testid="client-accept-bad"><p className="font-display text-xl text-zinc-50">This invite link isn't valid</p><p className="mt-2 text-sm text-zinc-400">It may have expired or already been used. Ask your RPOPRO contact to resend it, or sign in if you've already set up your account.</p><button onClick={() => nav("/client/login")} className="mt-5 text-sm text-[#F3C649] hover:underline" data-testid="client-accept-signin">Go to client sign in</button></Shell>;
  return (
    <Shell testid="client-accept">
      <h1 className="font-display text-2xl font-semibold text-zinc-50">Welcome{inv.name ? `, ${inv.name.split(" ")[0]}` : ""}</h1>
      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-zinc-400"><Building2 className="h-4 w-4 text-[#D4AF37]" />{inv.company || "Your"} workspace is ready.</p>
      <ul className="my-4 space-y-1 text-xs text-zinc-300" data-testid="client-accept-modules">{(inv.module_labels || []).map((m) => <li key={m} className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />{m}</li>)}</ul>
      <GoogleButton onClick={google} testid="client-accept-google" />
      <p className="mt-1.5 text-center text-[10px] text-zinc-500">Use Google with {inv.email || "the invited email"}</p>
      <Or />
      <form onSubmit={go} className="space-y-4" data-testid="client-accept-form" noValidate>
        <p className="rounded-xl border border-[#27272A] bg-[#09090B] px-3 py-2.5 text-sm text-zinc-300" data-testid="client-accept-email">{inv.email}</p>
        <div><Field label="Create a password" type="password" value={p} onChange={(x) => { setP(x); setErr(""); }} testid="client-accept-password" autoComplete="new-password" /><Strength p={p} /></div>
        <Field label="Confirm password" type="password" value={c} onChange={(x) => { setC(x); setErr(""); }} testid="client-accept-confirm" autoComplete="new-password" />
        <ErrorNote msg={err} />
        <GoldButton busy={busy} testid="client-accept-submit">Open my workspace</GoldButton>
      </form>
    </Shell>
  );
};
